'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { PLAYER_KEY } from './use-onboarding-state';

/**
 * Post-connect "welcome tail" — fires once per connected wallet after
 * the first-time player tour has been dismissed. Steps are:
 *
 *   1. Connect X (Privy `linkTwitter`).
 *   2. Say hi on X (optional tweet intent).
 *   3. Pick a player plinth (persisted via `/api/users/:addr/plinth`).
 *
 * Progress is remembered per-address in `localStorage` so switching
 * wallets re-prompts, but the demo user never sees the flow twice.
 *
 * The hook deliberately has no notion of wallet or twitter state — it
 * only tracks "did we finish the tail for this address yet?" Callers
 * decide whether the UI is eligible to open (e.g. require `ready` +
 * `isConnected` before consulting `shouldOpen`).
 */

const DONE_KEY_PREFIX = 'takeover-welcome-tail-done-v1:';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function safeRead(key: string): string | null {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore quota / privacy-mode errors
  }
}

function safeRemove(key: string) {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function doneKeyFor(address: string | null): string | null {
  if (!address) return null;
  return `${DONE_KEY_PREFIX}${address.toLowerCase()}`;
}

interface WelcomeTailControl {
  /** True when the overlay should be mounted + visible. */
  shouldOpen: boolean;
  /** Mark the tail finished for the current address. Safe to call once
   *  the user either completes every step or explicitly skips. */
  complete: () => void;
  /** Close the overlay without flipping the done flag — e.g. the user
   *  dismisses to browse and we'll re-prompt next session. */
  dismissForNow: () => void;
  /** QA: clear the done flag for the current address so the tail
   *  re-fires on next connect. */
  reset: () => void;
}

interface UseWelcomeTailOptions {
  /** Canonical wallet address (lower-cased internally). `null` while
   *  the user hasn't connected yet. */
  address: string | null;
  /** True once Privy is resolved AND the wallet is connected. The tail
   *  stays closed while this is false so we don't flash it before the
   *  session is ready. */
  enabled: boolean;
  /** While the first-run player tour modal is open, keep the tail closed.
   *  Must be wired from the parent so we re-evaluate eligibility the
   *  moment the tour dismisses (localStorage alone does not trigger a
   *  re-render). */
  playerTourOpen?: boolean;
}

export function useWelcomeTail({
  address,
  enabled,
  playerTourOpen = false,
}: UseWelcomeTailOptions): WelcomeTailControl {
  const [shouldOpen, setShouldOpen] = useState(false);
  // Tracks whether we've already asked the server if this address has a
  // plinth on file. The tail is only onboarding — once the DB knows their
  // plinth we don't ever want to prompt again (even across sessions).
  const dbCheckRef = useRef<string | null>(null);

  // Evaluate eligibility whenever the address, enablement, or player
  // tour visibility flips. The tour check used to live only in
  // localStorage reads inside this effect without `playerTourOpen` in
  // deps — that missed the transition when the tour finished in-session,
  // and could interact badly with stacked overlays.
  useEffect(() => {
    if (!enabled || !address) {
      setShouldOpen(false);
      return;
    }

    if (playerTourOpen) {
      setShouldOpen(false);
      return;
    }

    // Don't fire the tail until the player tour has been dismissed once.
    const playerDone = safeRead(PLAYER_KEY) === '1';
    if (!playerDone) {
      setShouldOpen(false);
      return;
    }

    const key = doneKeyFor(address);
    if (!key) {
      setShouldOpen(false);
      return;
    }

    if (safeRead(key) === '1') {
      setShouldOpen(false);
      return;
    }

    // Optimistic open while the DB lookup resolves — keeps the UX
    // identical for truly new wallets. If the server reports that
    // this address already has a plinth on file, we immediately flip
    // the local "done" flag so the tail never returns.
    setShouldOpen(true);

    const lowered = address.toLowerCase();
    if (dbCheckRef.current === lowered) return;
    dbCheckRef.current = lowered;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/users/${lowered}/plinth`);
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as {
          data?: { plinthId?: string | null };
        };
        if (cancelled) return;
        if (json.data?.plinthId) {
          safeWrite(key, '1');
          setShouldOpen(false);
        }
      } catch {
        // Network error — leave the overlay open; retry happens next session.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [address, enabled, playerTourOpen]);

  const complete = useCallback(() => {
    const key = doneKeyFor(address);
    if (key) safeWrite(key, '1');
    setShouldOpen(false);
  }, [address]);

  const dismissForNow = useCallback(() => {
    setShouldOpen(false);
  }, []);

  const reset = useCallback(() => {
    const key = doneKeyFor(address);
    if (key) safeRemove(key);
    setShouldOpen(true);
  }, [address]);

  return { shouldOpen, complete, dismissForNow, reset };
}
